import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import AddPatientModal from './AddPatientModal';
import { FiSearch } from 'react-icons/fi';
import '../styles/PatientsPage.css';
import '../styles/PatientGroups.css';

const PatientsPage = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'lastName', direction: 'asc' });
  const { currentUser, patientGroups } = useAuth();

  // Memoize the fetch function to prevent it from being recreated on every render
  const fetchAllPatients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching patients from Firestore...');
      // Query all users with role 'patient'
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', '==', 'patient'));
      
      const querySnapshot = await getDocs(q);
      const patientsList = [];
      
      querySnapshot.forEach((doc) => {
        console.log('Found patient:', { id: doc.id, ...doc.data() });
        patientsList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`Fetched ${patientsList.length} patients`);
      setPatients(patientsList);
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError('Failed to load patients. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Sort patients based on current sort configuration
  const filteredAndSortedPatients = useMemo(() => {
    let result = [...patients];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(patient => {
        const fullName = `${patient.firstName || ''} ${patient.lastName || ''}`.toLowerCase();
        return (
          fullName.includes(term) ||
          (patient.email && patient.email.toLowerCase().includes(term)) ||
          (patient.phone && patient.phone.includes(term)) ||
          (patient.phoneNumber && patient.phoneNumber.includes(term))
        );
      });
    }
    
    // Apply group filter
    if (selectedGroup && selectedGroup !== 'all') {
      result = result.filter(patient => 
        patient.groups && patient.groups.includes(selectedGroup)
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue, bValue;
        
        // Handle special case for name sorting
        if (sortConfig.key === 'name') {
          aValue = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
          bValue = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
        } else {
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return result;
  }, [patients, searchTerm, selectedGroup, sortConfig]);

  // Handle sort request
  const requestSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Get sort indicator for table headers
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };
  
  // Handle group filter change
  const handleGroupFilterChange = (e) => {
    setSelectedGroup(e.target.value);
  };
  
  // Get unique groups that have at least one patient assigned
  const groupsWithPatients = useMemo(() => {
    const groupSet = new Set();
    patients.forEach(patient => {
      if (patient.groups && Array.isArray(patient.groups)) {
        patient.groups.forEach(group => {
          if (group) groupSet.add(group);
        });
      }
    });
    return Array.from(groupSet).sort();
  }, [patients]);

  // Get filtered patients for display
  const filteredPatients = useMemo(() => {
    return filteredAndSortedPatients;
  }, [filteredAndSortedPatients]);

  useEffect(() => {
    // Only fetch if we have a current user
    if (currentUser) {
      fetchAllPatients();
    } else {
      setLoading(false);
    }
  }, [currentUser, fetchAllPatients]); // Add fetchAllPatients as a dependency

  if (loading) {
    return (
      <div className="loading-state">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4">Loading patients...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state">
        <svg className="h-12 w-12 text-red-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <p className="text-red-700 font-medium">Error loading patients</p>
        <p className="mt-2 text-sm">{error}</p>
      </div>
    );
  }

  const handleViewPatient = (e, patientId) => {
    e.stopPropagation();
    console.log('Navigating to patient profile:', patientId);
    navigate(`/clinician/patients/${patientId}`);
  };

  const handlePatientAdded = (newPatient) => {
    setPatients(prevPatients => [...prevPatients, newPatient]);
    setShowAddPatientModal(false);
  };

  const handleDeletePatient = async (e, patientId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this patient? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'users', patientId));
        // Refresh the patient list after deletion
        const q = query(collection(db, 'users'), where('role', '==', 'patient'));
        const querySnapshot = await getDocs(q);
        const patientsList = [];
        querySnapshot.forEach((doc) => {
          patientsList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        setPatients(patientsList);
      } catch (error) {
        console.error('Error deleting patient:', error);
        setError('Failed to delete patient. Please try again.');
      }
    }
  };

  return (
    <div className="patients-container">
      <div className="patients-header">
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">
            Patients <span className="text-gray-500 font-normal patient-number">({filteredAndSortedPatients.length})</span>
          </h2>
        </div>
        <div className="patients-actions">
          <div className="filters-container">
            <div className="search-container">
              <div className="search-input-container">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search patients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
            
            <div className="group-filter-container">
              <label htmlFor="groupFilter" className="group-filter-label">Filter by Group:</label>
              <select
                id="groupFilter"
                value={selectedGroup}
                onChange={handleGroupFilterChange}
                className="group-filter-select"
              >
                <option value="all">All Groups</option>
                {groupsWithPatients.map(group => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button 
            className="add-patient-btn"
            onClick={() => setShowAddPatientModal(true)}
          >
            + Add New Patient
          </button>
        </div>
      </div>
      
      {showAddPatientModal && (
        <AddPatientModal 
          onClose={() => setShowAddPatientModal(false)}
          onPatientAdded={handlePatientAdded}
        />
      )}
      
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 bg-gray-50">
          {/* Removed duplicate header */}
        </div>
        
        <div className="border-t border-gray-200">
          <dl>
            {filteredPatients.length === 0 ? (
              <div className="empty-state">
                <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p>No patients found{searchTerm ? ' matching your search' : ''}.</p>
                <p className="mt-2 text-sm text-gray-500">Add a new patient to get started</p>
                <button
                  onClick={() => navigate('/patient-profile/new')}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Add Your First Patient
                </button>
              </div>
            ) : (
              <table className="patients-table">
                <thead>
                  <tr>
                    <th 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('name')}
                    >
                      <div className="flex items-center">
                        Name
                        <span className="ml-1 text-sm">{getSortIndicator('name')}</span>
                      </div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('email')}
                    >
                      <div className="flex items-center">
                        Email
                        <span className="ml-1 text-sm">{getSortIndicator('email')}</span>
                      </div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('createdAt')}
                    >
                      <div className="flex items-center">
                        Created
                        <span className="ml-1 text-sm">{getSortIndicator('createdAt')}</span>
                      </div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('phone')}
                    >
                      <div className="flex items-center">
                        Phone
                        <span className="ml-1 text-sm">{getSortIndicator('phone')}</span>
                      </div>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPatients.map((patient) => (
                    <tr 
                      key={patient.id}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={(e) => handleViewPatient(e, patient.id)}
                    >
                      <td>
                        <div className="patient-name">
                          <div className="patient-avatar">
                            {patient.firstName?.[0]}{patient.lastName?.[0]}
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium">
                              {patient.firstName} {patient.lastName}
                            </span>
                            {patient.createdBy === 'self_registered' && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-normal bg-purple-100 text-purple-800">
                                self-registered
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{patient.email || 'N/A'}</td>
                      <td>
                        {patient.createdAt 
                          ? new Date(patient.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'N/A'}
                      </td>
                      <td>{patient.phone || patient.phoneNumber || 'N/A'}</td>
                      <td>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewPatient(e, patient.id);
                          }}
                          className="action-btn hover:bg-blue-100 p-1 rounded"
                          title="View Patient"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePatient(e, patient.id);
                          }}
                          className="action-btn delete ml-2 hover:bg-red-100 p-1 rounded"
                          title="Delete Patient"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </dl>
        </div>
      </div>
      
      {/* Debug information - uncomment if needed
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="text-lg font-medium text-blue-800 mb-2">Debug Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium">Current User:</p>
            <pre className="mt-1 p-2 bg-white rounded border overflow-auto max-h-32">
              {JSON.stringify({
                id: currentUser?.uid,
                email: currentUser?.email,
                role: currentUser?.role
              }, null, 2)}
            </pre>
          </div>
          <div>
            <p className="font-medium">Patients Data:</p>
            <pre className="mt-1 p-2 bg-white rounded border overflow-auto max-h-32">
              {JSON.stringify({
                totalPatients: patients.length,
                firstPatient: patients[0] || 'None'
              }, null, 2)}
            </pre>
          </div>
        </div>
      </div>
      */}
    </div>
  );
};

export default PatientsPage;
